import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { ApiError, apiFetch } from '../api/client.ts';
import { queryDefaults, useMe } from '../api/queries.ts';
import { useSession } from '../auth/session.tsx';
import type { ClientBanner } from '../api/types.ts';
import {
  PAGE_TABS, closeTab, oldStorageKey, openTab, parseStored, sameTab, serialise, storageKey, tabForPath, tabKey, tabPath,
  type PageKey, type Tab,
} from '../tabs/model.ts';

/**
 * Open clients and pages, as tabs (docs/navigation-brief.md, section 4; P-30: a tab is a client OR a page).
 *
 * - Opening a client's record opens a tab; opening a page in PAGE_TABS (Triage) opens its tab; several can be open;
 *   each closes.
 * - They last for the session in THIS browser tab: sessionStorage, keyed by the
 *   signed-in staff id, so they survive moving between modules and a reload,
 *   and never reach another viewer, another device or the server.
 * - **Only ids are stored, never a name** (a client's id, or "page:<key>"; tabs/model.ts). A client tab's label is
 *   read live, through the same banner every client page reads, as the reader. So a tab cannot show
 *   a name the reader may no longer see. A page tab's label is a fixed word.
 * - **A client tab the reader can no longer open closes quietly.** If that read
 *   answers 403 or 404 (an ended cover, a reassignment), the tab is dropped
 *   with no message: a refusal on a tab strip would itself say something about
 *   a client the reader cannot see.
 * - **A page tab's state is kept in memory only** (kept/keep): what the page was showing, so its tab returns to it
 *   exactly. Never in the address and never in storage (Triage's reason is free text); gone on a reload, on a staff
 *   change and when the tab is closed.
 */

interface OpenTabsValue {
  tabs: Tab[];
  open: (tab: Tab) => void;
  close: (tab: Tab) => void;
  kept: <T>(page: PageKey) => T | undefined;
  keep: (page: PageKey, value: unknown) => void;
}

const Ctx = createContext<OpenTabsValue>({ tabs: [], open: () => {}, close: () => {}, kept: () => undefined, keep: () => {} });

function read(staffId: string | undefined): Tab[] {
  if (!staffId) return [];
  try {
    const now = sessionStorage.getItem(storageKey(staffId));
    if (now !== null) return parseStored(now);
    // Before P-30 the list was client ids only, under its own key: read it once, so tabs open over the deploy stay.
    const before = parseStored(sessionStorage.getItem(oldStorageKey(staffId)));
    sessionStorage.setItem(storageKey(staffId), serialise(before));
    sessionStorage.removeItem(oldStorageKey(staffId));
    return before;
  } catch {
    return [];
  }
}

function write(staffId: string | undefined, tabs: Tab[]) {
  if (!staffId) return;
  try {
    sessionStorage.setItem(storageKey(staffId), serialise(tabs));
  } catch {
    // Private windows and blocked storage: the tabs still work for this page's life.
  }
}

export function OpenTabsProvider({ children }: { children: ReactNode }) {
  const me = useMe();
  const staffId = me.data?.id;
  const [tabs, setTabs] = useState<Tab[]>([]);
  const location = useLocation();
  // A page tab's state, in memory only: never written anywhere.
  const store = useRef(new Map<PageKey, unknown>());

  // Load this person's tabs once their id is known; a different person in the
  // same browser tab never sees another's, nor what another left on a page.
  useEffect(() => {
    store.current.clear();
    setTabs(read(staffId));
  }, [staffId]);

  const update = useCallback(
    (next: (prev: Tab[]) => Tab[]) =>
      setTabs((prev) => {
        const out = next(prev);
        write(staffId, out);
        return out;
      }),
    [staffId],
  );

  const open = useCallback((tab: Tab) => update((prev) => openTab(prev, tab)), [update]);
  const close = useCallback((tab: Tab) => {
    if (tab.kind === 'page') store.current.delete(tab.page);
    update((prev) => closeTab(prev, tab).list);
  }, [update]);
  const kept = useCallback(<T,>(page: PageKey) => store.current.get(page) as T | undefined, []);
  const keep = useCallback((page: PageKey, value: unknown) => { store.current.set(page, value); }, []);

  // Visiting a client's record, or a page in PAGE_TABS, opens its tab.
  useEffect(() => {
    const t = tabForPath(location.pathname);
    if (t && staffId) open(t);
  }, [location.pathname, staffId, open]);

  const value = useMemo(() => ({ tabs, open, close, kept, keep }), [tabs, open, close, kept, keep]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOpenTabs() {
  return useContext(Ctx);
}

/**
 * A page tab's state, kept while its tab is open (P-30): starts from what was kept, else `initial`, and every change
 * is kept. In memory only.
 */
export function useKeptState<T>(page: PageKey, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const { kept, keep } = useOpenTabs();
  const [value, setValue] = useState<T>(() => kept<T>(page) ?? initial);
  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const out = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      keep(page, out);
      return out;
    });
  }, [keep, page]);
  return [value, set];
}

const tabClass = (active: boolean) => `flex shrink-0 items-stretch rounded-t-md border border-b-0 ${
  active ? 'border-line border-t-[3px] border-t-accent bg-panel' : 'border-transparent hover:bg-panel/60'
}`;

function CloseButton({ name, onClose }: { name: string; onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label={`Close ${name}`}
      className="flex min-h-[44px] min-w-[44px] items-center justify-center text-neutral-600 hover:text-neutral-900"
    >
      <svg viewBox="0 0 20 20" aria-hidden="true" className="size-4">
        <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="1.8" />
      </svg>
    </button>
  );
}

/** The strip of open clients and pages, under the toolbar. Nothing at all when none is open. */
export function OpenTabsStrip() {
  const { tabs, close } = useOpenTabs();
  const { getToken } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const current = tabForPath(location.pathname);
  const clientIds = useMemo(() => tabs.flatMap((t) => (t.kind === 'client' ? [t.id] : [])), [tabs]);

  const banners = useQueries({
    queries: clientIds.map((id) => ({
      queryKey: ['client', id, 'banner'],
      queryFn: () => apiFetch<ClientBanner>(`/clients/${id}`, getToken),
      ...queryDefaults,
    })),
  });

  // Close, quietly, every client tab the reader can no longer open.
  useEffect(() => {
    banners.forEach((b, i) => {
      const err = b.error;
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        // The tab goes; a page already open on that client keeps its own refusal, which says why.
        close({ kind: 'client', id: clientIds[i]! });
      }
    });
  }, [banners, clientIds, close]);

  if (tabs.length === 0) return null;

  const shut = (tab: Tab, active: boolean) => {
    const { goTo } = closeTab(tabs, tab);
    close(tab);
    if (active) navigate(goTo);
  };

  return (
    <nav aria-label="Open tabs" className="border-b border-line bg-tint">
      <ul className="no-scrollbar mx-auto flex max-w-[96rem] gap-1 overflow-x-auto px-2 pt-1.5 lg:px-6">
        {tabs.map((tab) => {
          const active = current !== null && sameTab(current, tab);
          if (tab.kind === 'page') {
            const name = PAGE_TABS[tab.page].label;
            return (
              <li key={tabKey(tab)} data-page-tab={tab.page} className={tabClass(active)}>
                <Link
                  to={tabPath(tab)}
                  aria-current={active ? 'page' : undefined}
                  className="flex min-h-[44px] max-w-[16rem] items-center gap-2 pl-3 text-sm"
                >
                  <span className={`truncate ${active ? 'font-semibold text-neutral-900' : 'text-neutral-700'}`}>{name}</span>
                </Link>
                <CloseButton name={name} onClose={() => shut(tab, active)} />
              </li>
            );
          }
          const id = tab.id;
          const b = banners[clientIds.indexOf(id)];
          // Never a name from anywhere but a successful read as this reader.
          if (!b?.data) {
            if (b?.error) return null;
            return (
              <li key={id} className="flex min-h-[44px] items-center px-3 text-sm text-neutral-600">
                Opening…
              </li>
            );
          }
          const name = b.data.display_name;
          return (
            <li key={id}
                // The open client: the panel surface, with its top edge in the accent.
                className={tabClass(active)}>
              <Link
                to={`/clients/${id}`}
                aria-current={active ? 'page' : undefined}
                className="flex min-h-[44px] max-w-[16rem] items-center gap-2 pl-3 text-sm"
              >
                <span className="font-mono text-neutral-600 tabular-nums">{b.data.client_ref}</span>
                <span className={`truncate ${active ? 'font-semibold text-neutral-900' : 'text-neutral-700'}`}>{name}</span>
                {Number(b.data.critical_flags) > 0 && (
                  <span className="rounded bg-critical-bg px-1.5 text-xs font-bold text-critical-fg">Critical</span>
                )}
              </Link>
              <CloseButton name={name} onClose={() => shut(tab, active)} />
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
